import axiosClient from "./axiosClient";

const REQUEST_OPTIONS = {
  __silentGenericToast: true,
  __silent403: true,
  __suppressQuotaRedirect: true,
};

export class WhatsAppFlowsApiError extends Error {
  constructor(message, { code, status, warnings = [] } = {}) {
    super(message);
    this.name = "WhatsAppFlowsApiError";
    this.code = code || "WHATSAPP_FLOW_REQUEST_FAILED";
    this.status = status || 0;
    this.warnings = Array.isArray(warnings) ? warnings : [];
  }
}

export function unwrapWhatsAppFlowResponse(response) {
  const envelope = response?.data ?? {};
  const success = envelope?.success ?? envelope?.Success;
  if (success === false) {
    throw new WhatsAppFlowsApiError(
      envelope?.message ?? envelope?.Message ?? "WhatsApp Forms request failed.",
      {
        code: envelope?.code ?? envelope?.Code,
        status: response?.status,
        warnings: envelope?.warnings ?? envelope?.Warnings,
      },
    );
  }

  return envelope?.payload ??
    envelope?.Payload ??
    envelope?.data ??
    envelope?.Data ??
    envelope;
}

function normalizeWhatsAppFlowError(error) {
  if (error instanceof WhatsAppFlowsApiError) return error;

  const body = error?.response?.data ?? {};
  const status = Number(error?.response?.status || 0);
  return new WhatsAppFlowsApiError(
    body?.message ??
      body?.Message ??
      (status === 403
        ? "You do not have permission to perform this WhatsApp Forms action."
        : "WhatsApp Forms request failed. Please try again."),
    {
      code:
        body?.code ??
        body?.Code ??
        body?.errorCode ??
        (status === 403
          ? "WHATSAPP_FLOW_PERMISSION_DENIED"
          : "WHATSAPP_FLOW_REQUEST_FAILED"),
      status,
      warnings: body?.warnings ?? body?.Warnings,
    },
  );
}

async function request(config) {
  try {
    return unwrapWhatsAppFlowResponse(
      await axiosClient({
        ...REQUEST_OPTIONS,
        ...config,
      }),
    );
  } catch (error) {
    throw normalizeWhatsAppFlowError(error);
  }
}

const providerPath = "/whatsapp-flows/provider";
const localPath = "/whatsapp-flows";

const RESPONSE_TYPE_NAMES = {
  text: "Text",
  integer: "Integer",
  decimal: "Decimal",
  email: "Email",
  phone: "Phone",
  date: "Date",
  boolean: "Boolean",
  json: "Json",
};

export const normalizeWhatsAppFlowResponseType = value => {
  const normalized = String(value || "").trim().toLowerCase();
  return RESPONSE_TYPE_NAMES[normalized] || String(value || "").trim();
};

const normalizeSelectorItems = items =>
  (Array.isArray(items) ? items : []).map(item => ({
    ...item,
    fields: (Array.isArray(item?.fields) ? item.fields : []).map(field => ({
      ...field,
      responseType: normalizeWhatsAppFlowResponseType(field?.responseType),
    })),
  }));

export const listProviderFlows = after =>
  request({
    method: "get",
    url: providerPath,
    params: after ? { after } : undefined,
  });

export const getProviderFlow = metaFlowId =>
  request({
    method: "get",
    url: `${providerPath}/${encodeURIComponent(metaFlowId)}`,
  });

export const importProviderFlow = metaFlowId =>
  request({
    method: "post",
    url: `${localPath}/import`,
    data: { metaFlowId },
  });

export const listImportedFlows = () =>
  request({
    method: "get",
    url: localPath,
  });

export const listWhatsAppFlowSelector = async () =>
  normalizeSelectorItems(await request({
    method: "get",
    url: `${localPath}/selector`,
  }));

export const listWhatsAppFlowDataExchangeSelector = () =>
  request({
    method: "get",
    url: `${localPath}/selector/data-exchange`,
  });

export const registerWhatsAppFlowDataExchange = data =>
  request({
    method: "post",
    url: `${localPath}/data-exchange/register`,
    data,
  });

export const getImportedFlow = id =>
  request({
    method: "get",
    url: `${localPath}/${encodeURIComponent(id)}`,
  });

export const resyncImportedFlow = id =>
  request({
    method: "post",
    url: `${localPath}/${encodeURIComponent(id)}/resync`,
  });

export const archiveImportedFlow = id =>
  request({
    method: "post",
    url: `${localPath}/${encodeURIComponent(id)}/archive`,
  });

const ERROR_MESSAGES = {
  WHATSAPP_FLOW_DATA_EXCHANGE_NOT_SUPPORTED:
    "Use Data Exchange registration instead of Endpointless import for this Flow.",
  WHATSAPP_FLOW_SCHEMA_UNSUPPORTED:
    "XploreByte could not identify a stable supported response schema for this Flow.",
  WHATSAPP_FLOW_IMPORT_CONFLICT:
    "Meta reports different content for this already imported Form. XploreByte did not modify the published local revision.",
  WHATSAPP_FLOW_NOT_PUBLISHED:
    "Only Forms already published in Meta can be imported.",
  WHATSAPP_FLOW_PROVIDER_VALIDATION_FAILED:
    "Meta reports validation errors for this Flow. Resolve them in Meta before importing.",
  WHATSAPP_FLOW_VALIDATION_FAILED:
    "Meta reports validation errors for this Flow. Resolve them in Meta before importing.",
  WHATSAPP_FLOW_ASSET_MISSING:
    "The published Flow JSON asset could not be found.",
  WHATSAPP_FLOW_ASSET_NOT_FOUND:
    "The published Flow JSON asset could not be found.",
  WHATSAPP_FLOW_ASSET_INVALID:
    "The published Flow asset is empty, too large, or invalid.",
  WHATSAPP_FLOW_META_SETUP_REQUIRED:
    "Connect or configure your WhatsApp Business Account before importing Forms.",
  WHATSAPP_FLOW_ACCESS_NOT_CONFIGURED:
    "Connect or configure your WhatsApp Business Account before importing Forms.",
  WHATSAPP_FLOW_META_REQUEST_FAILED:
    "Meta could not complete the WhatsApp Forms request. Please try again.",
  WHATSAPP_FLOW_PROVIDER_ERROR:
    "Meta could not complete the WhatsApp Forms request. Please try again.",
  WHATSAPP_FLOW_META_TIMEOUT:
    "Meta took too long to respond. Please try again.",
  WHATSAPP_FLOW_PROVIDER_RESPONSE_INVALID:
    "Meta returned an unsupported response. Please try again later.",
  WHATSAPP_FLOW_OPERATION_CONFLICT:
    "This action is blocked by the Form lifecycle or existing automation history.",
  WHATSAPP_FLOW_ARCHIVED:
    "This WhatsApp Form is archived in XploreByte.",
  WHATSAPP_FLOW_NOT_FOUND:
    "The requested WhatsApp Form was not found.",
  WHATSAPP_FLOW_PERMISSION_DENIED:
    "You do not have permission to perform this WhatsApp Forms action.",
};

export function getWhatsAppFlowErrorMessage(error) {
  return (
    ERROR_MESSAGES[error?.code] ||
    error?.message ||
    "WhatsApp Forms request failed. Please try again."
  );
}
